import React, { useState, useEffect, useMemo } from 'react';
import useApiControl from '../../../common/useApiControl';
import BaseForm from '../BaseForm';
import { TextField, Autocomplete, MenuItem, Button, Box, Typography } from '@mui/material';
import FormFieldBlock from '../FormFieldBlock';
import ConfirmActionDialog from '../../dialogs/ConfirmActionDialog';
import { getBetDisplayLabel } from '../../room/betLabels';
import { ROOM_DISPLAY_PHASE } from '../../room/useRoomPhase';
import { isPositiveIntegerText } from '../../../common/plannedRoundCount';

const BONUS_OPTION_TIMING_RATE = 'timing_rate';
const RESULT_CONFIRMATION_METHOD = {
  MEMBER: 'member_confirmation',
  OWNER: 'owner_confirmation',
};
const BET_CLOSE_MODE = {
  TIMER: 'timer',
  MANUAL: 'manual',
};
const RESULT_CONFIRMATION_METHOD_LABEL = {
  [RESULT_CONFIRMATION_METHOD.MEMBER]: 'メンバー確認方式',
  [RESULT_CONFIRMATION_METHOD.OWNER]: 'オーナー確定方式',
};
const MIN_BET_CHOICES = 2;
const MAX_BET_CHOICES = 4;
const MAX_BET_TIME_HOURS = 72;
const MAX_TIME_PART = 59;
const MAX_BET_TIME_SECONDS = (MAX_BET_TIME_HOURS * 60 * 60) + (MAX_TIME_PART * 60) + MAX_TIME_PART;
const EVENT_DESCRIPTION_MAX_LENGTH = 140;
const DEFAULT_EVENT_DESCRIPTION = '【結果の確認・判定】速報サイトや公式Xなど、結果を確認する情報源を入力\n【参考情報】開催日時、会場、視聴方法などを入力\n【補足・注意事項】中止・延期など、通常どおり判定できない場合の扱いを入力';
const RATE_CALCULATION_TYPE = {
  REALTIME: 'realtime',
  FINAL: 'final',
};
const RATE_CALCULATION_HELPER_TEXT = {
  [RATE_CALCULATION_TYPE.REALTIME]: 'ベットした時点のレートで確定します。「いつベットするか」も予想の判断材料にしたい場合におすすめです。',
  [RATE_CALCULATION_TYPE.FINAL]: 'ベット受付終了後にレートを確定します。ベットするタイミングによる有利・不利を抑えたい場合におすすめです。',
};
const BET_CLOSE_MODE_HELPER_TEXT = {
  [BET_CLOSE_MODE.TIMER]: '設定した受付時間が経過すると、自動でベット受付を終了します。試合を観戦しながら「開始後10分間」など、あらかじめ受付時間を決めて予想を楽しむ場合におすすめです。',
  [BET_CLOSE_MODE.MANUAL]: 'オーナーが結果登録を開始するまで、ベット受付を継続します。観戦しながら、展開に合わせて受付を終了したい予想におすすめです。',
};
const RESULT_CONFIRMATION_HELPER_TEXT = {
  [RESULT_CONFIRMATION_METHOD.MEMBER]: 'オーナーの結果登録後、参加者の確認を経て結果を確定します。判定に参加者の確認を取り入れたいルームにおすすめです。',
  [RESULT_CONFIRMATION_METHOD.OWNER]: 'オーナーが登録した結果を、そのまま確定結果とします。結果が明確で、テンポよく次のラウンドへ進めたいルームにおすすめです。',
};

const OwnerForm = (props) => {
  const initialBetTime = Number(props.event.bet_time) || 0;
  const eventName = props.event.name;
  const [description, setDescription] = useState(props.event.description ?? '');

  const [startMargin, setStartMargin] = useState(props.event.margin);
  const [betTimeHours, setBetTimeHours] = useState(String(Math.floor(initialBetTime / 3600)));
  const [betTimeMinutes, setBetTimeMinutes] = useState(String(Math.floor((initialBetTime % 3600) / 60)));
  const [betTimeSeconds, setBetTimeSeconds] = useState(String(initialBetTime % 60));
  const [plannedRoundCount, setPlannedRoundCount] = useState(String(props.event.planned_round_count ?? 1));
  const [betCloseMode, setBetCloseMode] = useState(props.event.bet_close_mode ?? BET_CLOSE_MODE.TIMER);
  const [tags, setTags] = useState(Array.isArray(props.event.tags) ? props.event.tags : []);
  const [rateCalculationType, setRateCalculationType] = useState(
    Array.isArray(props.event.bonus_options) && props.event.bonus_options.includes(BONUS_OPTION_TIMING_RATE)
      ? RATE_CALCULATION_TYPE.REALTIME
      : RATE_CALCULATION_TYPE.FINAL
  );
  const resultConfirmationMethodLabel =
    RESULT_CONFIRMATION_METHOD_LABEL[props.event.result_confirmation_method] ?? RESULT_CONFIRMATION_METHOD_LABEL[RESULT_CONFIRMATION_METHOD.MEMBER];

  const [round2, setRound] = useState(props.round);

  const [rtype, setRoundType] = useState(round2.type);
  const parseAnswers = (rawAnswers) => {
    if (Array.isArray(rawAnswers)) {
      return rawAnswers;
    }

    if (typeof rawAnswers === 'string') {
      try {
        return JSON.parse(rawAnswers);
      } catch (e) {
        console.error('Error parsing answers JSON', e);
        console.log(rawAnswers);
        return [];
      }
    }

    return [];
  };

  const [answers, setAnswers] = useState(() => parseAnswers(round2.answers).slice(0, MAX_BET_CHOICES));
  const canEditAnswers =
    props.roomDisplayPhase === ROOM_DISPLAY_PHASE.INACTIVE &&
    props.round?.starttime == null;
  const [validationError, setValidationError] = useState(null);
  const [isConfirmDialogVisible, setConfirmDialogVisible] = useState(false);
  const [labelVisible, setLabelVisible] = useState(false);
  const [tagsValue, setTagsValue] = useState([]);
  const [masterTagOptions, setMasterTagOptions] = useState([]);
  // 時・分・秒入力の空欄は0秒扱いにして、既存APIのbettime秒パラメータへ合算する。
  const parseTimePart = (value) => {
    const parsed = value === '' ? 0 : Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  // type="number"でも直接入力では範囲外値を持てるため、整数かつ上限内だけstateへ反映する。
  const updateTimePart = (setter, max) => (value) => {
    if (!/^\d*$/.test(value)) {
      return;
    }
    if (value !== '' && Number(value) > max) {
      return;
    }
    setter(value);
  };
  const betTime =
    (parseTimePart(betTimeHours) * 60 * 60) +
    (parseTimePart(betTimeMinutes) * 60) +
    parseTimePart(betTimeSeconds);

  // フラグ管理
  const [optionsReadyStatus, setOptionsReadyStatus] = useState(0);
  const [tagsReadyStatus, setTagsReadyStatus] = useState(0);



  const params1 = useMemo(() => ({
    event_id: props.event?.id,
    number: props.event?.round + 1
  }), [props.event]);

  //round
  const { data: data1, loading: loading1, executeRequest: executeRequest1 } = useApiControl({
    action: '/get_round',
    params: params1,
    method: 'POST',
  });

  const updateRound = () => {
      executeRequest1().catch((error) => {
          console.error(error);
          // setExceptionOccurred(true);
      });
  };

  useEffect(() => {
      if (data1 && data1.length) {
          setRound(data1.round);
      }
  }, [data1]);
  useEffect(() => {
    const intervalId = setInterval(() => {
        const now = new Date();
        const starttime = new Date(round2.starttime);

        if (round2.starttime !== null) {
          if (now > starttime) {
            updateRound();
            clearInterval(intervalId);
            setLabelVisible(true);
          }
        }
    }, 1000);
    return () => {
      clearInterval(intervalId);
    }
  }, []);

  // マスタータグ取得
  const { data: data2, loading: loading2, executeRequest: executeRequest2 } = useApiControl({
    action: '/get_master_tags',
    method: 'POST',
  });

  useEffect(() => {
    executeRequest2();
  }, []);

  useEffect(() => {
    if (data2 && data2.tags && tags) {
      //マスタ登録
      setMasterTagOptions(
        data2.tags.map((tag, index) => ({
          label: tag.name,
          value: tag.id,
        }))
      );
    }
  }, [data2]);

  useEffect(() => {
    if (masterTagOptions.length) {
      setOptionsReadyStatus(1); // options が準備完了
    }
  }, [masterTagOptions]);

  // 初期化時にprops.event.tagsを分割して配列に変換
  useEffect(() => {
    if (Array.isArray(props.event.tags)) {
      console.log(props.event.tags);
      setTags(props.event.tags);
      setTagsReadyStatus(1); // tags が準備開始
      return;
    }

    console.log(props.event.tags);
    setTags([]);
    setTagsReadyStatus(1); // tags が準備開始
  }, [props.event.tags]);

  useEffect(() => {
    setRateCalculationType(
      Array.isArray(props.event.bonus_options) && props.event.bonus_options.includes(BONUS_OPTION_TIMING_RATE)
        ? RATE_CALCULATION_TYPE.REALTIME
        : RATE_CALCULATION_TYPE.FINAL
    );
  }, [props.event.bonus_options]);

  useEffect(() => {
    setPlannedRoundCount(String(props.event.planned_round_count ?? 1));
  }, [props.event.planned_round_count]);

  useEffect(() => {
    if (rateCalculationType === RATE_CALCULATION_TYPE.FINAL) {
      setBetCloseMode(BET_CLOSE_MODE.TIMER);
    }
  }, [rateCalculationType]);

  useEffect(() => {
    if (tagsReadyStatus === 1 && tags) {
      setTagsReadyStatus(2); // tags が準備完了
    }
  }, [tagsReadyStatus, tags]);

  useEffect(() => {
    if (optionsReadyStatus === 1 && tagsReadyStatus === 2) {
      setTagsValue(tags.map((index) => masterTagOptions.find((option) => option.value === index)));
    }
  }, [optionsReadyStatus, tagsReadyStatus]);
  
  // round.answersの制御ここから
  const [choices, setChoices] = useState(() => {
    const parsedAnswers = parseAnswers(round2.answers);
    const initialChoices = parsedAnswers.slice(0, MAX_BET_CHOICES);
    return initialChoices.length >= MIN_BET_CHOICES
      ? initialChoices
      : [...initialChoices, ...Array(MIN_BET_CHOICES - initialChoices.length).fill('')];
  });
  // 初期化時にprops.round.answersを分割して配列に変換
  useEffect(() => {
    const parsedChoices = parseAnswers(props.round.answers);
    if (props.round.answers) {
      console.log(props.round.answers);
    }
    const nextChoices = parsedChoices.slice(0, MAX_BET_CHOICES);
    setChoices(
      nextChoices.length >= MIN_BET_CHOICES
        ? nextChoices
        : [...nextChoices, ...Array(MIN_BET_CHOICES - nextChoices.length).fill('')]
    );
    setAnswers(nextChoices.filter((choice) => choice !== ''));
  }, [props.round.answers]);

  // フェーズ遷移で編集不可になった場合は、未保存の入力を現在のround値へ戻す。
  useEffect(() => {
    if (canEditAnswers) {
      return;
    }

    const parsedChoices = parseAnswers(props.round.answers);
    const nextChoices = parsedChoices.slice(0, MAX_BET_CHOICES);
    setChoices(
      nextChoices.length >= MIN_BET_CHOICES
        ? nextChoices
        : [...nextChoices, ...Array(MIN_BET_CHOICES - nextChoices.length).fill('')]
    );
    setAnswers(nextChoices.filter((choice) => choice !== ''));
  }, [canEditAnswers, props.round.answers]);

  // 選択肢の追加
  const addChoice = () => {
    setChoices((currentChoices) =>
      currentChoices.length >= MAX_BET_CHOICES
        ? currentChoices
        : [...currentChoices, '']
    );
  };
  // 選択肢の削除
  const removeChoice = (index) => {
    setChoices((currentChoices) =>
      currentChoices.length <= MIN_BET_CHOICES
        ? currentChoices
        : currentChoices.filter((_, i) => i !== index)
    );
  };
  // 選択肢の更新
  const updateChoice = (index, value) => {
    const newChoices = [...choices];
    newChoices[index] = value;
    setChoices(newChoices);
  };

  // 選択肢の配列形式への変換
  const getValidChoices = () => {
    return choices
      .slice(0, MAX_BET_CHOICES)
      .map((choice) => choice.trim())
      .filter((choice) => choice !== '');
  };

  // 選択肢をupdate_eventのパラメータにセット
  useEffect(() => {
    setAnswers(getValidChoices());
  }, [choices]);

  useEffect(() => {
    if (validationError && answers.length >= MIN_BET_CHOICES && answers.length <= MAX_BET_CHOICES) {
      setValidationError(null);
    }
  }, [answers, validationError]);

  // useEffect(() => {
  //   debugger;
  //   console.log(tagsValue);
  // }, [tagsValue]);

  // submit API
  const { data, error, loading, executeRequest } = useApiControl({
    action: '/update_event',
    params: { 
      eid:props.event.id,
      name:eventName,
      description,
      round:props.round.number,
      planned_round_count:Number(plannedRoundCount),
      bet_close_mode:betCloseMode,
      rtype:0,// 仮
      answers:answers,
      margin:Number(startMargin),
      bettime:Number(betTime),
      tags:tags,
      bonus_options: rateCalculationType === RATE_CALCULATION_TYPE.REALTIME ? [BONUS_OPTION_TIMING_RATE] : [],
    },
    method: 'POST',
  });
  // const [registerError, setRegisterError] = useState('');

  // handleSubmit
  const handleSubmit = (e) => {
    e.preventDefault();
    console.log(answers);

    const errors = {};

    if (answers.length < MIN_BET_CHOICES || answers.length > MAX_BET_CHOICES) {
      errors.answers = [`ベット対象は${MIN_BET_CHOICES}〜${MAX_BET_CHOICES}件で設定してください。`];
    }

    if (!isPositiveIntegerText(plannedRoundCount)) {
      errors.planned_round_count = ['予定ラウンド数は1以上の整数で入力してください。'];
    }

    if (betTime > MAX_BET_TIME_SECONDS) {
      errors.bettime = ['ベット受付時間は72時間59分59秒以内で入力してください。'];
    }

    if (Object.keys(errors).length > 0) {
      setValidationError({
        response: {
          status: 422,
          data: { errors },
        },
      });
      return;
    }

    setValidationError(null);
    setConfirmDialogVisible(true);
  };

  const handleConfirmSubmit = () => {
    setConfirmDialogVisible(false);
    executeRequest()
    .then(() => {
      // リクエスト成功時の処理
      props.updateRound();
      props.submitAfter();


      // TODO ダイアログ予定
      console.log('update success');
    })
    .catch(error => {
      console.error(error);
      // エラー処理
    });
  };


    const tagsHandleChange = (event, value) => {
      setTags(value.map((option) => option.value));
      setTagsValue(value);
    };

  return (
    <>
      <BaseForm
        onSubmit={handleSubmit}
        submitLabel="更新"
        logMessage="owner Update Api start"
        apiError={validationError || error}
        disableEnterSubmit
      >
        <Typography component="h3" variant="h6" sx={{ fontWeight: 900, m: 0 }}>
          ルーム情報変更
        </Typography>
      <FormFieldBlock title="ベットルーム名" description="作成後は変更できません。">
        <TextField
          label="ルーム名"
          variant="outlined"
          value={eventName}
          disabled
        />
      </FormFieldBlock>
      <FormFieldBlock title="レート計算の種類" description={RATE_CALCULATION_HELPER_TEXT[rateCalculationType]}>
        <TextField
          select
          label="選択してください"
          variant="outlined"
          value={rateCalculationType}
          onChange={(e) => setRateCalculationType(e.target.value)}
        >
          <MenuItem value={RATE_CALCULATION_TYPE.REALTIME}>リアルタイムレート</MenuItem>
          <MenuItem value={RATE_CALCULATION_TYPE.FINAL}>最終集計レート</MenuItem>
        </TextField>
      </FormFieldBlock>
      {rateCalculationType === RATE_CALCULATION_TYPE.REALTIME && (
        <FormFieldBlock title="ベット受付方式" description={BET_CLOSE_MODE_HELPER_TEXT[betCloseMode]}>
          <TextField
            select
            label="選択してください"
            value={betCloseMode}
            onChange={(e) => setBetCloseMode(e.target.value)}
          >
            <MenuItem value={BET_CLOSE_MODE.TIMER}>時間で終了</MenuItem>
            <MenuItem value={BET_CLOSE_MODE.MANUAL}>オーナー操作で終了</MenuItem>
          </TextField>
        </FormFieldBlock>
      )}
      {betCloseMode === BET_CLOSE_MODE.TIMER && (
        <FormFieldBlock title="ベット受付時間">
          <div style={{ display: 'flex', gap: '8px' }}>
            <TextField
              label="時間(0～72)"
              type="number"
              value={betTimeHours}
              InputProps={{ inputProps: { min: 0, max: MAX_BET_TIME_HOURS, step: 1 } }}
              onChange={(e) => updateTimePart(setBetTimeHours, MAX_BET_TIME_HOURS)(e.target.value)}
            />
            <TextField
              label="分(0～59)"
              type="number"
              value={betTimeMinutes}
              InputProps={{ inputProps: { min: 0, max: MAX_TIME_PART, step: 1 } }}
              onChange={(e) => updateTimePart(setBetTimeMinutes, MAX_TIME_PART)(e.target.value)}
            />
            <TextField
              label="秒(0～59)"
              type="number"
              value={betTimeSeconds}
              InputProps={{ inputProps: { min: 0, max: MAX_TIME_PART, step: 1 } }}
              onChange={(e) => updateTimePart(setBetTimeSeconds, MAX_TIME_PART)(e.target.value)}
            />
          </div>
        </FormFieldBlock>
      )}
      {rateCalculationType === RATE_CALCULATION_TYPE.REALTIME && betCloseMode === BET_CLOSE_MODE.MANUAL && (
        <FormFieldBlock title="レート変動の基準時間">
          <div style={{ display: 'flex', gap: '8px' }}>
            <TextField
              label="時間(0～72)"
              type="number"
              value={betTimeHours}
              InputProps={{ inputProps: { min: 0, max: MAX_BET_TIME_HOURS, step: 1 } }}
              onChange={(e) => updateTimePart(setBetTimeHours, MAX_BET_TIME_HOURS)(e.target.value)}
            />
            <TextField
              label="分(0～59)"
              type="number"
              value={betTimeMinutes}
              InputProps={{ inputProps: { min: 0, max: MAX_TIME_PART, step: 1 } }}
              onChange={(e) => updateTimePart(setBetTimeMinutes, MAX_TIME_PART)(e.target.value)}
            />
            <TextField
              label="秒(0～59)"
              type="number"
              value={betTimeSeconds}
              InputProps={{ inputProps: { min: 0, max: MAX_TIME_PART, step: 1 } }}
              onChange={(e) => updateTimePart(setBetTimeSeconds, MAX_TIME_PART)(e.target.value)}
            />
          </div>
        </FormFieldBlock>
      )}
      <FormFieldBlock
        title="結果の確定方式"
        description={`${RESULT_CONFIRMATION_HELPER_TEXT[props.event.result_confirmation_method] ?? RESULT_CONFIRMATION_HELPER_TEXT[RESULT_CONFIRMATION_METHOD.MEMBER]} 作成後は変更できません。`}
      >
        <TextField
          label="結果の確定方式"
          variant="outlined"
          value={resultConfirmationMethodLabel}
          disabled
        />
      </FormFieldBlock>
      <FormFieldBlock title="予定ラウンド数" description="参加者向け表示情報です。ラウンド単位で手動終了が行えます。">
        <TextField
          label="予定ラウンド数"
          type="number"
          variant="outlined"
          value={plannedRoundCount}
          onChange={(e) => setPlannedRoundCount(e.target.value)}
          InputProps={{ inputProps: { min: 1, step: 1 } }}
        />
      </FormFieldBlock>
      <FormFieldBlock title="補足情報" description={`${EVENT_DESCRIPTION_MAX_LENGTH}文字まで入力できます。`}>
        <TextField
          label="補足情報"
          variant="outlined"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={DEFAULT_EVENT_DESCRIPTION}
          multiline
          rows={3}
          inputProps={{ maxLength: EVENT_DESCRIPTION_MAX_LENGTH }}
        />
      </FormFieldBlock>
      {labelVisible && (
        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 700 }}>
          ※次のラウンドから変更されます
        </Typography>
      )}
      {/* <RadioGroup
        value={rtype}
        onChange={(e) => setRoundType(e.target.value)}
        label="パターン"
      > */}
        {/* <FormControlLabel value="0" control={<Radio />} label="選択" /> */}
        {/* <FormControlLabel value="1" control={<Radio />} label="数当て" /> */}
        {/* <FormControlLabel value="" control={<Radio />} label="Other" /> */}
      {/* </RadioGroup> */}

      <FormFieldBlock
        title="ベット対象の選択肢"
        description={canEditAnswers ? '2〜4件、各20文字まで入力できます。' : 'ベット対象はラウンド終了後、次のラウンド開始前に変更できます。'}
      >
        {Number(rtype) === 0 && choices.map((choice, index) => (
          <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              label={getBetDisplayLabel(index)}
              variant="outlined"
              value={choice}
              onChange={(e) => updateChoice(index, e.target.value)}
              margin="normal"
              fullWidth
              inputProps={{ maxLength: 20 }}
              disabled={!canEditAnswers}
            />
            <Button
              type="button"
              variant="outlined"
              color="inherit"
              disabled={!canEditAnswers || choices.length <= MIN_BET_CHOICES}
              onClick={() => removeChoice(index)}
              sx={{ flexShrink: 0, mt: 1, minWidth: 72 }}
            >
              削除
            </Button>
          </Box>
        ))}
        {Number(rtype) === 0 && (
          <Button
            type="button"
            variant="outlined"
            onClick={addChoice}
            disabled={!canEditAnswers || choices.length >= MAX_BET_CHOICES}
            sx={{ alignSelf: 'flex-start' }}
          >
            選択肢を追加
          </Button>
        )}
      </FormFieldBlock>
      <FormFieldBlock title="ラウンド開始までの待機時間">
        <TextField
          label="秒(0～99)"
          type="number"
          value={startMargin}
          InputProps={{ inputProps: { min: 0, max: 99 } }}
          onChange={(e) => setStartMargin(e.target.value)}
        />
      </FormFieldBlock>
      {/*
      暫定対応: ルーム作成時の固定値項目のため、設定画面では非表示にする。
      入力欄を復活させる場合はCreateForm側の固定値送信も合わせて戻す。
      <FormFieldBlock title="ルームチップの参加時所持数" description="変更できません。">
        <TextField
          value={props.event.entry_room_points}
          variant="outlined"
          disabled
        />
      </FormFieldBlock>

      <FormFieldBlock title="最小ベット額" description="変更できません。">
        <TextField
          value={props.event.minimum_bet}
          variant="outlined"
          disabled
        />
      </FormFieldBlock>

      <FormFieldBlock title="最大ベット額" description="変更できません。">
        <TextField
          value={props.event.max_bet}
          variant="outlined"
          disabled
        />
      </FormFieldBlock>
      */}
      <FormFieldBlock title="ルームのカテゴリ">
          <Autocomplete
            multiple
            options={masterTagOptions}
            value={tagsValue}
            getOptionLabel={(option) => option.label}
            renderInput={(params) => (
              <TextField {...params} label="複数選択" variant="outlined" />
            )}
            onChange={tagsHandleChange}
          />
        </FormFieldBlock>
      </BaseForm>
      <ConfirmActionDialog
        isVisible={isConfirmDialogVisible}
        onClose={() => setConfirmDialogVisible(false)}
        onConfirm={handleConfirmSubmit}
        loading={loading}
      />
    </>
  );
}
export default OwnerForm;
